import React, { useState } from 'react';
import { notification } from 'antd';
import { Icon, Select, Button } from 'Components';
import LMSButton from '../components/Button';
import styles from 'LearningManager/styles/CourseAssignments.module.scss';
import { Assignment } from 'LearningManager/constants/CoursesAssignment.types';
import AssignmentCard from '../components/AssignmentCard';
import CourseCard from '../components/CourseCard';
import FilterSearch from '../components/FilterSearch';
import ToggleGroup from 'LearningManager/components/ToggleGroup';
import course1 from 'LearningManager/images/course-1.png';
import course2 from 'LearningManager/images/course-2.png';
import course3 from 'LearningManager/images/course-3.jpg';
import course4 from 'LearningManager/images/course-4.jpg';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import UnassignTrainingModal from 'LearningManager/components/UnassignTrainingModal';
import UnassignScheduledModal, {
	UnassignTarget
} from 'LearningManager/components/UnassignScheduledModal';
import ViewDetailsDrawer from 'LearningManager/components/ViewDetailsDrawer';
import CourseDetailsDrawer from 'LearningManager/components/CourseDetailsDrawer';
import ResetCompletionModal, {
	ResetTarget
} from 'LearningManager/components/ResetCompletionModal';
import BulkActionsBar from 'LearningManager/components/BulkActionsBar';
import BulkResetModal from 'LearningManager/components/BulkResetModal';
import { StepperProvider } from 'LearningManager/hooks/Stepper.context';
import Heading from 'LearningManager/components/Heading';
import HeaderRow from 'Pages/TenantProvider/components/HeaderRow';

const { Option } = Select;

interface CourseItem {
	id: number;
	title: string;
	image: string;
	tags: string[];
	status?: 'Completed' | 'In Progress' | 'Not Started';
}

const MOCK_COURSES: CourseItem[] = [
	{ id: 1, title: '[Course name]', image: course1, tags: ['Required'], status: 'Completed' },
	{ id: 2, title: '[Course name]', image: course2, tags: ['Optional', 'Required'], status: 'In Progress' },
	{ id: 3, title: '[Course name]', image: course3, tags: ['Required'], status: 'Completed' },
	{ id: 4, title: '[Course name]', image: course4, tags: ['Optional'], status: 'Not Started' },
	{ id: 5, title: '[Course name]', image: course1, tags: ['Required'], status: 'In Progress' },
	{ id: 6, title: '[Course name]', image: course2, tags: ['Required'], status: 'Completed' },
	{ id: 7, title: '[Course name]', image: course3, tags: ['Optional', 'Scheduled'], status: 'Not Started' },
	{ id: 8, title: '[Course name]', image: course4, tags: ['Required'], status: 'In Progress' },
	{ id: 9, title: '[Course name]', image: course1, tags: ['Optional'], status: 'Completed' },
	{ id: 10, title: '[Course name]', image: course2, tags: ['Required'], status: 'Not Started' },
	{ id: 11, title: '[Course name]', image: course3, tags: ['Scheduled'], status: 'In Progress' },
	{ id: 12, title: '[Course name]', image: course4, tags: ['Required'], status: 'Completed' },
	{ id: 13, title: '[Course name]', image: course1, tags: ['Optional'], status: 'Not Started' },
	{ id: 14, title: '[Course name]', image: course2, tags: ['Required'], status: 'In Progress' },
	{ id: 15, title: '[Course name]', image: course3, tags: ['Required', 'Scheduled'], status: 'Completed' },
	{ id: 16, title: '[Course name]', image: course4, tags: ['Optional'], status: 'Not Started' },
	{ id: 17, title: '[Course name]', image: course1, tags: ['Required'], status: 'In Progress' },
	{ id: 18, title: '[Course name]', image: course2, tags: ['Optional'], status: 'Completed' },
	{ id: 19, title: '[Course name]', image: course3, tags: ['Required'], status: 'Not Started' },
	{ id: 20, title: '[Course name]', image: course4, tags: ['Scheduled'], status: 'In Progress' }
];

const MOCK_ASSIGNMENTS: Assignment[] = [
	{
		id: '1',
		name: '[Assignment Name C]',
		type: 'One-Time',
		setupType: 'Quick Setup',
		status: 'Scheduled',
		courseCount: 4
	},
	{
		id: '2',
		name: '[Assignment Name C]',
		type: 'One-Time',
		setupType: 'Quick Setup',
		courseCount: 2
	},
	{
		id: '3',
		name: '[Assignment Name C]',
		type: 'Ongoing',
		setupType: 'Custom Setup',
		status: 'Scheduled',
		courseCount: 9
	},
	{
		id: '4',
		name: '[Assignment Name D]',
		type: 'Ongoing',
		setupType: 'Custom Setup',
		courseCount: 8
	}
];

const CourseAssignments: React.FC = () => {
	const [activeTab, setActiveTab] = useState<string>('1');
	const [selectedOrg, setSelectedOrg] = useState<string>('all');
	const [filterSearch, setFilterSearch] = useState<string>('');
	const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
	const [checkedCourseIds, setCheckedCourseIds] = useState<Set<number>>(
		new Set()
	);
	const [assignments, setAssignments] = useState<Assignment[]>(
		MOCK_ASSIGNMENTS
	);
	const [unassignTarget, setUnassignTarget] = useState<Assignment | null>(
		null
	);
	const [detailTarget, setDetailTarget] = useState<Assignment | null>(null);
	const [courseDetailTarget, setCourseDetailTarget] = useState<CourseItem | null>(null);
	const [resetTarget, setResetTarget] = useState<Assignment | null>(null);
	const [courseResetTarget, setCourseResetTarget] = useState<CourseItem | null>(null);
	const [courseUnassignTarget, setCourseUnassignTarget] = useState<CourseItem | null>(null);
	const [showBulkReset, setShowBulkReset] = useState(false);
	const [showBulkUnassign, setShowBulkUnassign] = useState(false);
	const history = useHistory();
	const { url } = useRouteMatch();

	const isSelectionActive = checkedIds.size > 0;

	const handleSelectAll = () => {
		setCheckedIds(new Set(assignments.map((a) => a.id)));
	};

	const handleDeselectAll = () => {
		setCheckedIds(new Set());
	};

	const handleBulkReset = () => {
		setShowBulkReset(true);
	};

	const handleBulkResetConfirm = () => {
		setShowBulkReset(false);
		setCheckedIds(new Set());
		notification.open({
			message: 'Training reset',
			placement: 'topRight',
			duration: 3,
			className: 'lms-notification',
			closeIcon: <></>,
			icon: <Icon type="infoOutlined" role="img" />,
			description: null
		});
	};

	const handleBulkUnassign = () => {
		setShowBulkUnassign(true);
	};

	const handleBulkUnassignConfirm = () => {
		setAssignments((prev) => prev.filter((a) => !checkedIds.has(a.id)));
		setCheckedIds(new Set());
		setShowBulkUnassign(false);
		notification.open({
			message: 'Training unassigned',
			placement: 'topRight',
			duration: 3,
			className: 'lms-notification',
			closeIcon: <></>,
			icon: <Icon type="infoOutlined" role="img" />,
			description: null
		});
	};
	const { search } = useLocation();
	const params = new URLSearchParams(search);
	const showNoAssignments = params.has('showNoAssignments');
	const showNoFilterMatches = params.has('showNoFilterMatches');

	const handleCheck = (id: string, checked: boolean) => {
		setCheckedIds((prev) => {
			const next = new Set(prev);
			checked ? next.add(id) : next.delete(id);
			return next;
		});
	};

	const handleViewContent = (id: string) => {
		console.log('View content:', id);
	};

	const handleMenuAction = (id: string, action: string) => {
		const assignment = assignments.find((item) => item.id === id);

		if (action === 'edit') {
			const setupType = assignment?.setupType ?? 'Quick Setup';
			const params = new URLSearchParams({
				setupType,
				assignmentId: id
			});
			if (assignment?.status === 'Scheduled') {
				params.set('isScheduled', 'true');
			}

			history.push(`${url}/create-assignment?${params.toString()}`);
			return;
		}

		if (action === 'view-details' && assignment) {
			setDetailTarget(assignment);
			return;
		}

		if (action === 'unassign' && assignment) {
			setUnassignTarget(assignment);
			return;
		}

		if (action === 'reset-completion' && assignment) {
			setResetTarget(assignment);
			return;
		}

		console.log('Menu action:', action, 'on assignment:', id);
	};

	const handleConfirmUnassign = () => {
		if (!unassignTarget) return;

		setAssignments((prev) =>
			prev.filter((assignment) => assignment.id !== unassignTarget.id)
		);
		setCheckedIds((prev) => {
			const next = new Set(prev);
			next.delete(unassignTarget.id);
			return next;
		});
		setUnassignTarget(null);
		notification.open({
			message: 'Training unassigned',
			placement: 'topRight',
			duration: 3,
			className: 'lms-notification',
			closeIcon: <></>,
			icon: <Icon type="infoOutlined" role="img" />,
			description: null
		});
	};

	const handleExport = () => {
		console.log('Export triggered');
	};

	const handleNewAssignment = () => {
		history.push(`${url}/create-assignment`);
	};

	return (
		<div className={styles.page}>
			<div className={styles.header}>
				<HeaderRow
					title="Course assignments"
					subHeading="Create, manage and track training assignments"
				>
					{!showNoAssignments && (
						<div className={styles.header__actions}>
							<LMSButton
								variant="border"
								size="small"
								onClick={handleExport}
							>
								Export
							</LMSButton>
							<Button
								size="small"
								className="cta-prominent"
								type="primary"
								icon={<Icon type="plus" role="img" />}
								onClick={handleNewAssignment}
							>
								New assignment
							</Button>
						</div>
					)}
				</HeaderRow>
			</div>

			{showNoAssignments ? (
				<div className={styles.emptyState}>
					<div className={styles.emptyState__icon}>
						<Icon type="noAssignments" role="img" />
					</div>
					<div className={styles.emptyState__title}>
						No assignments found
					</div>
					<div className={styles.emptyState__description}>
						It looks like your organization doesn&apos;t have any
						assignments yet. Create an assignment to deliver
						training to your users.
					</div>
					<LMSButton
						type="primary"
						size="small"
						onClick={handleNewAssignment}
					>
						Create assignment
					</LMSButton>
				</div>
			) : (
				<>
					<div className={styles.filtersContainer}>
						<ToggleGroup
							value={activeTab}
							onChange={(value) => setActiveTab(value)}
							options={[
								{ key: '1', label: 'Assignments' },
								{ key: '2', label: 'Courses' }
							]}
						/>

						<Select
							value={selectedOrg}
							onChange={(val) => {
								setSelectedOrg(val);
								setFilterSearch('');
							}}
							style={{ width: 180, color: '#000' }}
							size="small"
							className={styles.orgSelect}
						>
							<Option value="all">All organization</Option>
							<Option value="roles">Roles</Option>
							<Option value="property">Property</Option>
							<Option value="individual">Individual</Option>
						</Select>
					</div>

					{selectedOrg !== 'all' && (
						<FilterSearch
							filterType={selectedOrg}
							value={filterSearch}
							onChange={setFilterSearch}
						/>
					)}

					{!showNoFilterMatches && (
						<div className={styles.listHeader}>
							<span className={styles.listHeader__label}>
								{activeTab === '1'
									? `Assignments by organization (
								${assignments.length})`
									: `	Courses by organization (${MOCK_COURSES.length})`}
							</span>
							{isSelectionActive && (
								<BulkActionsBar
									selectedCount={checkedIds.size}
									onSelectAll={handleSelectAll}
									onDeselectAll={handleDeselectAll}
									onReset={handleBulkReset}
									onUnassign={handleBulkUnassign}
								/>
							)}
						</div>
					)}

					{showNoFilterMatches ? (
						<div className={styles.emptyState}>
							<div className={styles.emptyState__icon}>
								<Icon type="filterNoAssignments" role="img" />
							</div>
							<div className={styles.emptyState__title}>
								No assignments match these filters
							</div>
							<div className={styles.emptyState__description}>
								Adjust the filters or search to see more
								results.
							</div>
						</div>
					) : (
						<>
							{activeTab === '1' && (
								<div className={styles.list}>
									{assignments.map((assignment) => (
										<AssignmentCard
											key={assignment.id}
											assignment={assignment}
											checked={checkedIds.has(
												assignment.id
											)}
											menuDisabled={isSelectionActive}
											onCheck={handleCheck}
											onViewContent={handleViewContent}
											onMenuAction={handleMenuAction}
										/>
									))}
								</div>
							)}

							{activeTab === '2' && (
								<>
									<div className={styles.courseGrid}>
										{MOCK_COURSES.map((course) => (
											<CourseCard
												key={course.id}
												index={course.id}
												course={course}
												onClick={() =>
													setCourseDetailTarget(course)
												}
												showCheckbox
												checked={checkedCourseIds.has(
													course.id
												)}
												onCheck={(id, isChecked) =>
													setCheckedCourseIds(
														(prev) => {
															const next = new Set(
																prev
															);
															isChecked
																? next.add(id)
																: next.delete(
																		id
																  );
															return next;
														}
													)
												}
												showMenu
												onMenuAction={(id, action) => {
													const target = MOCK_COURSES.find((c) => c.id === id);
													if (action === 'view-details' && target) {
														setCourseDetailTarget(target);
													} else if (action === 'reset-completion' && target) {
														setCourseResetTarget(target);
													} else if (action === 'unassign' && target) {
														setCourseUnassignTarget(target);
													} else {
														console.log('Course action:', action, id);
													}
												}}
											/>
										))}
									</div>
								</>
							)}
						</>
					)}
				</>
			)}

			<div className={styles.footer}>Powered by Venza</div>
			<StepperProvider>
				<UnassignTrainingModal
					visible={
						Boolean(unassignTarget) &&
						unassignTarget?.status !== 'Scheduled'
					}
					onSubmit={handleConfirmUnassign}
					onCancel={() => setUnassignTarget(null)}
				/>
				<UnassignScheduledModal
					visible={
						Boolean(unassignTarget) &&
						unassignTarget?.status === 'Scheduled'
					}
					onCancel={() => setUnassignTarget(null)}
					onContinue={(_target: UnassignTarget) => {
						handleConfirmUnassign();
					}}
				/>
				<ViewDetailsDrawer
					visible={Boolean(detailTarget)}
					detailTarget={detailTarget}
					onClose={() => setDetailTarget(null)}
				/>
				<CourseDetailsDrawer
					visible={Boolean(courseDetailTarget)}
					course={courseDetailTarget}
					onClose={() => setCourseDetailTarget(null)}
				/>
				<ResetCompletionModal
					visible={Boolean(resetTarget)}
					onCancel={() => setResetTarget(null)}
					onContinue={(_target: ResetTarget) => {
						setResetTarget(null);
						notification.open({
							message: 'Training reset',
							placement: 'topRight',
							duration: 3,
							className: 'lms-notification',
							closeIcon: <></>,
							icon: <Icon type="infoOutlined" role="img" />,
							description: null
						});
					}}
				/>
				<BulkResetModal
					visible={showBulkReset}
					selectedCount={checkedIds.size}
					totalUserCount={assignments.length}
					onCancel={() => setShowBulkReset(false)}
					onConfirm={handleBulkResetConfirm}
				/>
				<UnassignTrainingModal
					visible={showBulkUnassign}
					assignmentCount={checkedIds.size}
					learnerCount={assignments
						.filter((a) => checkedIds.has(a.id))
						.reduce((sum, a) => sum + a.courseCount, 0)}
					onSubmit={handleBulkUnassignConfirm}
					onCancel={() => setShowBulkUnassign(false)}
				/>
				<BulkResetModal
					visible={Boolean(courseResetTarget)}
					entityType="course"
					selectedCount={2}
					totalUserCount={20}
					onCancel={() => setCourseResetTarget(null)}
					onConfirm={() => {
						setCourseResetTarget(null);
						notification.open({
							message: 'Training reset',
							placement: 'topRight',
							duration: 3,
							className: 'lms-notification',
							closeIcon: <></>,
							icon: <Icon type="infoOutlined" role="img" />,
							description: null
						});
					}}
				/>
				<UnassignTrainingModal
					visible={Boolean(courseUnassignTarget)}
					courseCount={2}
					learnerCount={20}
					onSubmit={() => {
						setCourseUnassignTarget(null);
						notification.open({
							message: 'Training unassigned',
							placement: 'topRight',
							duration: 3,
							className: 'lms-notification',
							closeIcon: <></>,
							icon: <Icon type="infoOutlined" role="img" />,
							description: null
						});
					}}
					onCancel={() => setCourseUnassignTarget(null)}
				/>
			</StepperProvider>
		</div>
	);
};

export default CourseAssignments;
